import { useInfiniteQuery } from "@tanstack/react-query";
import api from "./api";

// Cursor-paginated customer activity feed (BFF: GET api/customers/{id}/activities,
// backed by activities-service). Returns { items, nextCursor }.
async function fetchCustomerActivities(customerId, { cursor, limit = 20, eventNames, signal } = {}) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (limit) params.set("limit", String(limit));
  if (eventNames?.length) params.set("eventNames", eventNames.join(","));
  const queryString = params.toString();

  const { data } = await api.get(
    `api/customers/${customerId}/activities${queryString ? `?${queryString}` : ""}`,
    { signal },
  );
  return data; // { items: [...], nextCursor }
}

// Unfiltered reads keep the bare ["customerActivities", id] key — the optimistic prepends write to it —
// while a filtered read gets its own cache entry under that prefix, so invalidations still reach it.
function useCustomerActivities(customerId, { eventNames } = {}) {
  return useInfiniteQuery({
    queryKey: eventNames?.length
      ? ["customerActivities", customerId, { eventNames }]
      : ["customerActivities", customerId],
    queryFn: ({ pageParam, signal }) =>
      fetchCustomerActivities(customerId, { cursor: pageParam, eventNames, signal }),
    enabled: !!customerId,
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

// Merchant-wide activity across all customers (BFF: GET api/activities). Returns
// { items, nextCursor } — the backfill for the Today's-tasks activity feed. `aiSuggested`
// restricts to AI-suggestion outcomes as far as the user's role scope reaches (own / branch /
// merchant — decided server-side); `from` is the oldest occurredAt to include.
async function fetchMerchantActivities({ eventNames, aiSuggested, from, cursor, limit = 30, signal } = {}) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (limit) params.set("limit", String(limit));
  if (eventNames) params.set("eventNames", eventNames);
  if (aiSuggested) params.set("aiSuggested", "true");
  if (from) params.set("from", from instanceof Date ? from.toISOString() : from);
  const queryString = params.toString();

  const { data } = await api.get(
    `api/activities${queryString ? `?${queryString}` : ""}`,
    { signal },
  );
  return data; // { items: [...], nextCursor }
}

export { fetchCustomerActivities, useCustomerActivities, fetchMerchantActivities };
